import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  closestCenter, MeasuringStrategy,
  type DragEndEvent,
  type DragStartEvent,
  type Modifier,
} from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../lib/api";
import { useHotkey } from "../lib/keybindings";
import { useRenameRequest } from "../lib/renameRequest";

interface Props {
  tabs: string[];                 // request IDs in tab order
  activeId: string | null;        // request ID from URL (might be missing)
  onClose: (id: string) => void;
  onReorder: (next: string[]) => void;
}

// Modeled on the dnd-kit "horizontal sortable" basic-setup story: a single
// SortableContext + horizontalListSortingStrategy, paired with a
// DragOverlay so the dragged pill follows the cursor smoothly without
// being clipped by the strip's overflow.
const restrictToHorizontalAxis: Modifier = ({ transform }) => ({ ...transform, y: 0 });

export default function TabStrip({ tabs, activeId, onClose, onReorder }: Props) {
  const nav = useNavigate();
  const [dragId, setDragId] = useState<string | null>(null);

  useHotkey("Shift+Tab", () => {
    if (tabs.length < 2) return;
    const idx = activeId ? tabs.indexOf(activeId) : -1;
    const next = tabs[(idx + 1) % tabs.length];
    if (next) {
      nav({ pathname: `/r/${next}`, search: window.location.search });
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
  });

  useHotkey("Mod+Shift+w", () => {
    if (!activeId) return;
    const idx = tabs.indexOf(activeId);
    if (idx < 0) return;
    const remaining = tabs.filter((t) => t !== activeId);
    onClose(activeId);
    if (remaining.length === 0) { nav({ pathname: "/", search: window.location.search }); return; }
    const neighbour = remaining[Math.min(idx, remaining.length - 1)];
    nav({ pathname: `/r/${neighbour}`, search: window.location.search });
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = navigator.platform.match(/Mac|iPhone|iPad/) ? e.metaKey : e.ctrlKey;
      if (!mod || e.shiftKey || e.altKey) return;
      const n = parseInt(e.key, 10);
      if (!Number.isFinite(n) || n < 1 || n > 9) return;
      const target = tabs[n - 1];
      if (target) {
        e.preventDefault();
        nav({ pathname: `/r/${target}`, search: window.location.search });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [tabs, nav]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  if (tabs.length === 0) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      // Always-measure ensures the rects stay current as the strip scrolls.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={(e: DragStartEvent) => setDragId(String(e.active.id))}
      onDragCancel={() => setDragId(null)}
      onDragEnd={(e: DragEndEvent) => {
        setDragId(null);
        if (!e.over || e.active.id === e.over.id) return;
        const from = tabs.indexOf(String(e.active.id));
        const to = tabs.indexOf(String(e.over.id));
        if (from < 0 || to < 0) return;
        onReorder(arrayMove(tabs, from, to));
      }}
    >
      <SortableContext items={tabs} strategy={horizontalListSortingStrategy}>
        <div className="h-9 flex items-stretch overflow-x-auto scroll-pane border-b border-border bg-surface">
          {tabs.map((id) => (
            <SortableTabPill
              key={id}
              requestId={id}
              active={id === activeId}
              onSelect={() => nav({ pathname: `/r/${id}`, search: window.location.search })}
              onClose={() => {
                const idx = tabs.indexOf(id);
                const remaining = tabs.filter((t) => t !== id);
                onClose(id);
                if (id === activeId) {
                  const search = window.location.search;
                  if (remaining.length === 0) nav({ pathname: "/", search });
                  else nav({ pathname: `/r/${remaining[Math.min(idx, remaining.length - 1)]}`, search });
                }
              }}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.2, 0, 0, 1)" }}>
        {dragId ? <TabPillOverlay requestId={dragId} active={dragId === activeId} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

// Per-row data hook so both the live pill and the overlay can share the
// same request lookup without duplicating fetches.
function useRequestSummary(id: string) {
  return useQuery({
    queryKey: ["request", id],
    queryFn: () => api.requests.get(id),
    staleTime: 60_000,
  });
}

function SortableTabPill({ requestId, active, onSelect, onClose }: { requestId: string; active: boolean; onSelect: () => void; onClose: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: requestId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Hide the original slot while the overlay is in flight.
    opacity: isDragging ? 0 : 1,
  };
  const q = useRequestSummary(requestId);
  const r = q.data?.request;
  const method = (r?.method ?? "GET").toLowerCase();
  const name = r?.name ?? "Loading…";
  const rename = useRenameRequest();
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={
        "shrink-0 flex items-center gap-2 px-3 border-r border-border text-sm cursor-pointer select-none outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft relative " +
        // Accent bottom border on the active tab so it's obvious which
        // request is open. Inactive tabs keep a transparent border the
        // same width so heights match.
        (active
          ? "bg-bg text-text border-b-2 border-b-accent"
          : "text-text-dim hover:text-text hover:bg-surface-2 border-b-2 border-b-transparent")
      }
      onClick={onSelect}
      onAuxClick={(e) => { if (e.button === 1) onClose(); }}
      title={`${(r?.method ?? "GET")} ${r?.url ?? ""} — double-click to rename`}
    >
      <span className={"method-tag " + method}>{(r?.method ?? "GET").toUpperCase()}</span>
      <span
        className="max-w-[180px] truncate"
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (r) rename(requestId, r.name);
        }}
      >{name}</span>
      <button
        type="button"
        aria-label="Close tab"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="h-5 w-5 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-3"
      >×</button>
    </div>
  );
}

// Floating duplicate of the pill rendered by DragOverlay. Visually
// identical to the live pill but lifted with a shadow so it reads as
// the picked-up element.
function TabPillOverlay({ requestId, active }: { requestId: string; active: boolean }) {
  const q = useRequestSummary(requestId);
  const r = q.data?.request;
  return (
    <div
      className={
        "shrink-0 flex items-center gap-2 px-3 py-1.5 border-r border-border text-sm cursor-grabbing select-none shadow-[var(--shadow-strong)] rounded " +
        (active ? "bg-bg text-text" : "bg-surface text-text-dim")
      }
    >
      <span className={"method-tag " + (r?.method ?? "GET").toLowerCase()}>{(r?.method ?? "GET").toUpperCase()}</span>
      <span className="max-w-[180px] truncate">{r?.name ?? "…"}</span>
    </div>
  );
}
